import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import type { HostStudent, StudentProfile } from '../../../models/HostStudent';
import { hostApiClient } from '../../../hostApiClient';
import { useThemeContext } from '../../../contexts/ThemeContext';
import {
  isStudentMinor,
  toUpdateStudentProfileRequest,
  validateStudentProfileForm,
  type StudentProfileFormValues,
} from '../../../utils/studentProfile';

export interface StudentProfileDialogProps {
  open: boolean;
  student: HostStudent | null;
  onClose: () => void;
  onSaved: (studentUserId: string, profile: StudentProfile) => void;
}

function profileToForm(profile: StudentProfile | null | undefined): StudentProfileFormValues {
  return {
    firstName: profile?.firstName ?? '',
    lastName: profile?.lastName ?? '',
    dateOfBirth: profile?.dateOfBirth ?? '',
    guardianContact: profile?.guardianContact ?? '',
    notes: profile?.notes ?? '',
  };
}

const fieldSx = (mode: string) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
    },
    '&.Mui-focused': {
      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.07)' : '#ffffff',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 600,
  },
  '& .MuiFormHelperText-root': {
    mx: 0.25,
    mt: 0.75,
  },
});

function SectionLabel({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25 }}>
      <Box
        sx={{
          display: 'inline-flex',
          color: 'primary.main',
          opacity: 0.9,
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: '0.08em',
          fontWeight: 700,
          color: 'text.secondary',
          lineHeight: 1,
        }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

export function StudentProfileDialog({ open, student, onClose, onSaved }: StudentProfileDialogProps) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const sx = fieldSx(mode);
  const [form, setForm] = useState<StudentProfileFormValues>(() => profileToForm(student?.studentProfile));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof StudentProfileFormValues, string>>>(
    {}
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(profileToForm(student?.studentProfile));
    setFieldErrors({});
    setSubmitError(null);
  }, [open, student]);

  const guardianRequired = useMemo(() => isStudentMinor(form.dateOfBirth.trim() || null), [form.dateOfBirth]);

  const displayName =
    student?.name?.trim() ||
    student?.phoneNumber ||
    t('hostUsers.unnamedUser', 'Unnamed user');

  const schoolName = [form.firstName.trim(), form.lastName.trim()].filter(Boolean).join(' ');
  const avatarLetter = (schoolName || displayName).charAt(0).toUpperCase();

  const setField = (key: keyof StudentProfileFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!student) return;
    const errors = validateStudentProfileForm(form, {
      dateOfBirthFuture: t(
        'hostStudents.profile.dateOfBirthFuture',
        'Date of birth cannot be in the future.'
      ),
      guardianContactRequired: t(
        'hostStudents.profile.guardianContactRequired',
        'Guardian contact is required for students under 18.'
      ),
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const saved = await hostApiClient.updateHostStudentProfile(
        student.id,
        toUpdateStudentProfileRequest(form)
      );
      onSaved(student.id, saved);
      onClose();
    } catch {
      setSubmitError(
        t('hostStudents.profile.saveError', 'Could not save student profile. Try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          backgroundImage:
            mode === 'dark'
              ? `linear-gradient(165deg, ${alpha('#6366F1', 0.12)} 0%, transparent 42%)`
              : `linear-gradient(165deg, ${alpha('#6366F1', 0.06)} 0%, transparent 42%)`,
        },
      }}
    >
      <DialogTitle sx={{ pt: 2.75, px: 3, pb: 2 }}>
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Avatar
            src={student?.profileImageUrl || undefined}
            sx={{
              width: 48,
              height: 48,
              fontWeight: 800,
              bgcolor: alpha('#6366F1', mode === 'dark' ? 0.28 : 0.14),
              color: mode === 'dark' ? '#C7D2FE' : 'primary.dark',
              boxShadow: `0 6px 16px ${alpha('#000', mode === 'dark' ? 0.35 : 0.08)}`,
            }}
          >
            {avatarLetter}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                color: mode === 'dark' ? '#ECECFB' : '#212529',
              }}
            >
              {t('hostStudents.profile.editTitle', 'Edit student profile')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {t(
                'hostStudents.profile.editExplainer',
                'School profile for {{name}}. Used for tutor lessons in your organization.',
                { name: displayName }
              )}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {form.dateOfBirth ? (
                <Chip
                  size="small"
                  color={guardianRequired ? 'warning' : 'default'}
                  variant={guardianRequired ? 'filled' : 'outlined'}
                  label={
                    guardianRequired
                      ? t('hostStudents.profile.minor', 'Under 18')
                      : t('hostStudents.profile.adult', 'Adult')
                  }
                  sx={{ fontWeight: 700, height: 24 }}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('hostStudents.profile.incomplete', 'Profile incomplete')}
                  sx={{ fontWeight: 700, height: 24 }}
                />
              )}
              {student?.phoneNumber && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={student.phoneNumber}
                  sx={{ fontWeight: 600, height: 24 }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogTitle>

      <Divider
        sx={{
          mx: 3,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />

      <DialogContent sx={{ pt: 2.5, px: 3, pb: 1.5 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Stack spacing={2.75}>
          <Box>
            <SectionLabel icon={<PersonOutlineRoundedIcon />}>
              {t('hostStudents.profile.sectionIdentity', 'Identity')}
            </SectionLabel>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.75}>
                <TextField
                  label={t('hostStudents.profile.firstName', 'First name')}
                  value={form.firstName}
                  onChange={(e) => setField('firstName', e.target.value)}
                  fullWidth
                  disabled={submitting}
                  sx={sx}
                />
                <TextField
                  label={t('hostStudents.profile.lastName', 'Last name')}
                  value={form.lastName}
                  onChange={(e) => setField('lastName', e.target.value)}
                  fullWidth
                  disabled={submitting}
                  sx={sx}
                />
              </Stack>
              <TextField
                label={t('hostStudents.profile.dateOfBirth', 'Date of birth')}
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => setField('dateOfBirth', e.target.value)}
                fullWidth
                disabled={submitting}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldErrors.dateOfBirth)}
                helperText={fieldErrors.dateOfBirth}
                sx={sx}
              />
            </Stack>
          </Box>

          <Box>
            <SectionLabel icon={<ShieldOutlinedIcon />}>
              {t('hostStudents.profile.sectionGuardian', 'Guardian')}
            </SectionLabel>
            <TextField
              label={t('hostStudents.profile.guardianContact', 'Guardian contact')}
              placeholder={t(
                'hostStudents.profile.guardianContactPlaceholder',
                'Parent name, relationship, phone'
              )}
              value={form.guardianContact}
              onChange={(e) => setField('guardianContact', e.target.value)}
              fullWidth
              multiline
              minRows={guardianRequired ? 2 : 1}
              maxRows={4}
              disabled={submitting}
              required={guardianRequired}
              error={Boolean(fieldErrors.guardianContact)}
              helperText={
                fieldErrors.guardianContact ||
                (guardianRequired
                  ? t(
                      'hostStudents.profile.guardianContactHintRequired',
                      'Required because this student is under 18.'
                    )
                  : t(
                      'hostStudents.profile.guardianContactHintOptional',
                      'Optional for adult students.'
                    ))
              }
              sx={sx}
            />
          </Box>

          <Box>
            <SectionLabel icon={<NotesOutlinedIcon />}>
              {t('hostStudents.profile.sectionNotes', 'Notes')}
            </SectionLabel>
            <TextField
              label={t('hostStudents.profile.notes', 'Notes')}
              placeholder={t(
                'hostStudents.profile.notesPlaceholder',
                'Lesson preferences, schedule notes…'
              )}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              disabled={submitting}
              sx={sx}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.25,
          gap: 1,
          borderTop: 1,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
          bgcolor: mode === 'dark' ? alpha('#000', 0.18) : alpha('#6366F1', 0.03),
        }}
      >
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 1.5 }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !student}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            px: 2.25,
            borderRadius: 999,
            minWidth: 132,
            boxShadow: `0 8px 20px ${alpha('#6366F1', mode === 'dark' ? 0.35 : 0.28)}`,
          }}
        >
          {submitting ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            t('hostStudents.profile.save', 'Save profile')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
